import loadable from '@loadable/component';
import {
  BpkBreakpoint,
  BpkFlex,
  BpkVessel,
} from '@skyscanner-internal/backpack-web/bpk-component-layout';
import BpkMobileScrollContainer from '@skyscanner-internal/backpack-web/bpk-component-mobile-scroll-container';
import BpkSectionHeader from '@skyscanner-internal/backpack-web/bpk-component-section-header';
import {
  ACTION_TYPE,
  COMPONENT_NAME,
} from '@skyscanner-internal/landing-pages-shared-constants/constants';

import logMiniEventHelper from '../../../mini-event/logMiniEventHelper';
import DesktopScrollContainer from '../../common/DesktopScrollContainer/DesktopScrollContainer';
import { useMBDContext } from '../../common/MBD/useMBDContext';
import RatingsCard from '../RatingsCard/RatingsCard';

import type {
  ProviderRatingProps,
  ProviderInfo,
} from '@skyscanner-internal/landing-pages-shared-types/types-shared/ProviderRating';

import STYLES from './ProviderList.module.scss';

const IntersectionObserverWrapper = loadable(
  () => import('../../IntersectionObserverWrapper/IntersectionObserverWrapper'),
);

const ProviderList = (props: ProviderRatingProps) => {
  const {
    enableDesktopScroll,
    headerText,
    providerInfoList,
    strings,
    subHeaderText,
  } = props;
  const { buildMBDParam, rawComponentName } = useMBDContext();

  if (!providerInfoList?.length) {
    return null;
  }

  return (
    <BpkVessel
      className={STYLES.ProviderList}
      data-tracking-element-id={rawComponentName}
      data-tracking-common-params={buildMBDParam(rawComponentName)}
    >
      {headerText && (
        <BpkVessel
          className={`${STYLES.ProviderList__header} ${
            enableDesktopScroll
              ? STYLES.ProviderList__header__remainScrolllerSpace
              : ''
          }`}
        >
          <BpkSectionHeader title={headerText} description={subHeaderText} />
        </BpkVessel>
      )}
      {enableDesktopScroll ? (
        <DesktopScrollContainer nextLabel="next" prevLabel="prev">
          {providerInfoList.map((provider: ProviderInfo, index: number) => (
            <RatingsCard
              key={`${provider.providerName}-desktop`}
              strings={strings}
              provider={provider}
              index={index}
            />
          ))}
        </DesktopScrollContainer>
      ) : (
        <BpkVessel className={STYLES.ProviderList__desktop}>
          {providerInfoList.map((provider: ProviderInfo, index: number) => (
            <BpkVessel
              key={`${provider.providerName}-desktop`}
              className={STYLES.ProviderList__desktop__cardContainer}
            >
              <RatingsCard
                strings={strings}
                provider={provider}
                index={index}
              />
            </BpkVessel>
          ))}
        </BpkVessel>
      )}

      <BpkMobileScrollContainer
        className={STYLES.ProviderList__mobileScroll}
        trailingIndicatorClassName={STYLES.Indicator}
      >
        <BpkFlex
          wrap={{ base: 'nowrap', [BpkBreakpoint.SmallTablet]: 'wrap' }}
          justify="flex-start"
        >
          {providerInfoList.map((provider: ProviderInfo, index: number) => (
            <BpkVessel
              key={provider.providerName}
              className={STYLES.ProviderList__container}
            >
              <RatingsCard
                strings={strings}
                provider={provider}
                index={index}
              />
            </BpkVessel>
          ))}
        </BpkFlex>
      </BpkMobileScrollContainer>
    </BpkVessel>
  );
};

export default (props: ProviderRatingProps) => (
  <IntersectionObserverWrapper
    onElementSeen={() =>
      logMiniEventHelper({
        action_type: ACTION_TYPE.COMPONENT_VISIBLE,
        component_name: COMPONENT_NAME.PROVIDER_RATING,
      })
    }
  >
    <ProviderList {...props} />
  </IntersectionObserverWrapper>
);
